import { RECONCILIATION_LABELS } from '@/modules/projects/components/ReconciliationBadge'
import { RECONCILIATION_STATUSES } from '@/modules/projects/constants'
import { isSegmentHeldForTeam } from '@/modules/projects/status'
import type {
  ReconciliationAccount,
  ReconciliationStatus,
  Statement,
  StatementSegment,
} from '@/modules/projects/stores'
import { formatCompactDateRange, formatDate } from '@/utils'
import { formatAccountType } from '../../labels'
import {
  type AccountIdentity,
  accountKey,
  accountLabel,
  reconcilableSegments,
  reconcilableStatements,
  withShareLabel,
} from './helpers'

// One account inside one billing cycle: the unit the reconciliation table lists, sorts and searches.
// A cycle that covers three accounts is three rows, so every column holds a single real value.
export interface CycleAccountRow {
  id: string
  statementId: number
  segmentId: number
  status: ReconciliationStatus
  // The verdict as words. The table sorts, filters and searches on this, not on the wire enum.
  statusLabel: string
  reviewRank: number
  account: string
  accountType: string
  statement: string
  period: string
  // Blank when the cycle never got a real date, which sorts and filters it out of the calendar.
  periodStart: string
  periodEnd: string
  begin: number | null
  net: number | null
  end: number | null
  // Signed miss between the printed end balance and the transactions. Null when there is nothing to
  // report, so the cell reads as empty rather than a real $0.00.
  gap: number | null
  isDuplicate: boolean
  isManual: boolean
  isVerifying: boolean
}

// Needs-review first, then the ones nobody can settle, then the untouched, then the done. This is
// the table's default order and the order the Status column sorts by.
const REVIEW_RANK: Record<ReconciliationStatus, number> = {
  [RECONCILIATION_STATUSES.needsReview]: 0,
  [RECONCILIATION_STATUSES.unverifiable]: 1,
  [RECONCILIATION_STATUSES.unknown]: 2,
  [RECONCILIATION_STATUSES.partiallyReconciled]: 3,
  [RECONCILIATION_STATUSES.reconciled]: 4,
}

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}/

// A failed cycle stores "Unknown" where a date belongs, and that word sorts past every real date.
const calendarDate = (value: string | null | undefined) =>
  value && CALENDAR_DATE.test(value) ? value : ''

// What the Period column reads. Real calendar dates only, never the cycle's name: a failed cycle
// names itself after the file it came from, which is 80-odd characters of no use in a date column
// and, in a column sized to its widest value, drags every other row's dates out with it.
const periodOf = (segment: StatementSegment) => {
  const start = calendarDate(segment.startDate)
  const end = calendarDate(segment.endDate)
  if (start && end) return formatCompactDateRange(start, end)
  const known = start || end
  return known ? formatDate(known, 'MMM d, yyyy') : 'Unknown period'
}

// An account that printed balances carries its own verdict, which can differ from the cycle's: one
// account of three can be the only one that misses.
const accountStatus = (
  account: ReconciliationAccount,
): ReconciliationStatus => {
  if (account.reconciled) return RECONCILIATION_STATUSES.reconciled
  if (account.gap != null) return RECONCILIATION_STATUSES.needsReview
  return RECONCILIATION_STATUSES.unverifiable
}

// The accounts the engine saw but could not check, as identities. Prefer the full entries: they
// carry the share id, so two shares of one member number don't collapse into one row.
const uncheckedAccounts = (segment: StatementSegment): AccountIdentity[] => {
  const detail = segment.reconciliationDetail
  if (detail?.unverifiedAccounts?.length) {
    return detail.unverifiedAccounts.map((account) => ({
      last4: account.last4,
      shareNumber: account.shareNumber ?? null,
    }))
  }
  return (detail?.unverifiedLast4S ?? []).map((last4) => ({
    last4,
    shareNumber: null,
  }))
}

interface RowContext {
  statement: Statement
  segment: StatementSegment
}

const VERIFYING_LABEL = 'Verifying'

const baseRow = (
  { statement, segment }: RowContext,
  account: AccountIdentity,
  status: ReconciliationStatus,
): CycleAccountRow => ({
  id: `${segment.id}:${accountKey(account)}`,
  statementId: statement.id,
  segmentId: segment.id,
  status,
  statusLabel: isSegmentHeldForTeam(segment)
    ? VERIFYING_LABEL
    : RECONCILIATION_LABELS[status],
  reviewRank: REVIEW_RANK[status],
  account: withShareLabel(accountLabel(account.last4), account.shareNumber),
  accountType: '',
  statement: statement.fileName,
  period: periodOf(segment),
  periodStart: calendarDate(segment.startDate),
  periodEnd: calendarDate(segment.endDate),
  begin: null,
  net: null,
  end: null,
  gap: null,
  isDuplicate: segment.duplicateOfSegmentId != null,
  isManual: segment.manuallyReconciled === true,
  isVerifying: isSegmentHeldForTeam(segment),
})

const checkedRow = (
  context: RowContext,
  account: ReconciliationAccount,
): CycleAccountRow => ({
  ...baseRow(context, account, accountStatus(account)),
  accountType: formatAccountType(account.accountType),
  begin: account.begin,
  // A zero count means the statement printed this account but nothing ran through it, so a $0 net
  // would read as real activity.
  net: account.transactionCount === 0 ? null : account.sum,
  end: account.end,
  gap: account.reconciled ? null : account.gap,
})

// Every account this cycle covers, once each. The engine's checked accounts come first and win any
// repeat, because only they carry balances; the rest fill in from what the cycle's rows named.
const segmentRows = (
  statement: Statement,
  segment: StatementSegment,
): CycleAccountRow[] => {
  const context = { statement, segment }
  const rows = new Map<string, CycleAccountRow>()

  for (const account of segment.reconciliationDetail?.accounts ?? []) {
    const row = checkedRow(context, account)
    const sharing = rows.get(row.id)
    // A checking and its credit line can print under one number: the one that misses keeps the row.
    if (!sharing || row.reviewRank < sharing.reviewRank) rows.set(row.id, row)
  }
  for (const account of uncheckedAccounts(segment)) {
    const row = baseRow(context, account, RECONCILIATION_STATUSES.unverifiable)
    if (!rows.has(row.id)) rows.set(row.id, row)
  }
  for (const account of segment.accountNumbers ?? []) {
    const row = baseRow(context, account, segment.reconciliationStatus)
    if (!rows.has(row.id)) rows.set(row.id, row)
  }

  if (rows.size === 0) {
    const unknown = { last4: '', shareNumber: null }
    const row = baseRow(context, unknown, segment.reconciliationStatus)
    rows.set(row.id, row)
  }

  // The cycle's own printed balances belong to no single account, so they only stand in when there
  // is exactly one account to hand them to.
  const [only] = rows.values()
  if (rows.size === 1 && only.begin == null && only.end == null) {
    only.begin = segment.startBalance
    only.end = segment.endBalance
  }

  return withTheCyclesVerdict(segment, [...rows.values()])
}

// The rows never contradict their cycle. A cycle accepted by hand reads reconciled on every row. A
// cycle can need review for a reason no account carries (a doubtful cut, more rows than its register
// prints), and then every row shows that doubt.
const withTheCyclesVerdict = (
  segment: StatementSegment,
  rows: CycleAccountRow[],
): CycleAccountRow[] => {
  const reviewed = RECONCILIATION_STATUSES.needsReview
  if (isAcceptedByHand(segment)) {
    return rows.map((row) =>
      withStatus(row, RECONCILIATION_STATUSES.reconciled),
    )
  }
  const doubtOnNoAccount =
    segment.reconciliationStatus === reviewed &&
    !rows.some((row) => row.status === reviewed)
  return doubtOnNoAccount ? rows.map((row) => withStatus(row, reviewed)) : rows
}

const isAcceptedByHand = (segment: StatementSegment) =>
  segment.manuallyReconciled === true &&
  segment.reconciliationStatus === RECONCILIATION_STATUSES.reconciled

const withStatus = (
  row: CycleAccountRow,
  status: ReconciliationStatus,
): CycleAccountRow => ({
  ...row,
  status,
  statusLabel: row.isVerifying
    ? row.statusLabel
    : RECONCILIATION_LABELS[status],
  reviewRank: REVIEW_RANK[status],
  gap: status === RECONCILIATION_STATUSES.reconciled ? null : row.gap,
})

// Needs-review first, then oldest period, then account, so the work that needs a human is what the
// user lands on and the rest reads in calendar order. A cycle with no real date has no place on the
// calendar and goes last within its group.
const compareRows = (first: CycleAccountRow, second: CycleAccountRow) => {
  if (first.reviewRank !== second.reviewRank) {
    return first.reviewRank - second.reviewRank
  }
  if (first.periodStart !== second.periodStart) {
    if (first.periodStart === '') return 1
    if (second.periodStart === '') return -1
    return first.periodStart.localeCompare(second.periodStart)
  }
  return (
    first.account.localeCompare(second.account) ||
    first.id.localeCompare(second.id)
  )
}

// Every cycle of every reconcilable statement, one row per account, in the table's default order.
export const cycleAccountRows = (statements: Statement[]): CycleAccountRow[] =>
  reconcilableStatements(statements)
    .flatMap((statement) =>
      reconcilableSegments(statement).flatMap((segment) =>
        segmentRows(statement, segment),
      ),
    )
    .sort(compareRows)

export interface CycleRef {
  statementId: number
  segmentId: number
}

// Every billing cycle on the page flattened into one worklist, in the order the table lists them by
// default, so the workspace's previous / next walks past the end of a statement into the next one.
export const cycleWorklist = (statements: Statement[]): CycleRef[] => {
  const seen = new Set<number>()
  const cycles: CycleRef[] = []
  for (const row of cycleAccountRows(statements)) {
    if (seen.has(row.segmentId)) continue
    seen.add(row.segmentId)
    cycles.push({ statementId: row.statementId, segmentId: row.segmentId })
  }
  return cycles
}

// How many cycle-accounts sit at each verdict, for the tally strip over the list.
export const countByStatusLabel = (
  rows: CycleAccountRow[],
): Map<string, number> => {
  const counts = new Map<string, number>()
  for (const row of rows) {
    counts.set(row.statusLabel, (counts.get(row.statusLabel) ?? 0) + 1)
  }
  return counts
}
